import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import { useTranslation } from 'react-i18next';
import { TestingConversationMessage } from './TestingConversationMessage';
import { STOP_AUTOSCROLL_THRESHOLD_PX } from './constants';
import { useStyles } from './styles';
import {
  TestingConversationViewMessage,
  useTestingConversationMessages,
} from './useTestingConversationMessages';

const getTranscriptionInterval = (
  currentMessage: TestingConversationViewMessage,
  previousMessage?: TestingConversationViewMessage,
): number | undefined => {
  if (
    typeof currentMessage.firstReceivedTime !== 'number' ||
    typeof previousMessage?.lastReceivedTime !== 'number'
  ) {
    return undefined;
  }

  return Math.max(
    0,
    Math.floor((currentMessage.firstReceivedTime - previousMessage.lastReceivedTime) / 1000),
  );
};

type PlaceholderMode = 'waiting' | 'empty';

interface TestingConversationViewProps {
  agentName?: string;
  messages?: TestingConversationViewMessage[];
  placeholderMode?: PlaceholderMode;
}

export const TestingConversationView: React.FC<TestingConversationViewProps> = ({
  agentName = 'Agent',
  messages,
  placeholderMode = 'waiting',
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesBoxRef = useRef<HTMLDivElement | null>(null);
  const fallbackMessages = useTestingConversationMessages();
  const allMessages = messages ?? fallbackMessages;
  const isWaitingPlaceholder = placeholderMode === 'waiting';

  useEffect(() => {
    if (!messagesBoxRef.current || !messagesEndRef.current) return;

    const { clientHeight, scrollTop, scrollHeight } = messagesBoxRef.current;
    const isNearBottom = scrollHeight - STOP_AUTOSCROLL_THRESHOLD_PX <= clientHeight + scrollTop;

    if (isNearBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages]);

  useEffect(() => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <Box className={classes.dialogGridContent} id="testing-dialog-grid-content">
      <Box className={classes.chatBox}>
        {allMessages.length !== 0 ? (
          <Box ref={messagesBoxRef} className={classes.chatBoxMessages}>
            {allMessages.map((messageItem, index) => (
              <TestingConversationMessage
                key={messageItem.id || index}
                actorName={messageItem.actor === 'agent' ? agentName : 'You'}
                messageItem={messageItem}
                isStacked={messageItem.actor === allMessages[index - 1]?.actor}
                timeSincePreviousMessage={getTranscriptionInterval(
                  messageItem,
                  allMessages[index - 1],
                )}
              />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        ) : (
          <Box className={classes.chatBoxMessages}>
            <Box className={classes.emptyState}>
              <Box className={classes.emptyStateIcon}>
                <HeadsetMicIcon sx={{ fontSize: 28, color: '#9ca3af' }} />
              </Box>
              <Typography className={classes.emptyStateTitle}>
                {t(
                  isWaitingPlaceholder
                    ? 'campaignPage.conversationNotStartedYet'
                    : 'campaignPage.emptyConversationHistory',
                )}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
